import { useState, useCallback, useMemo } from "react";
import {
  SelectedSupplier,
  PartQuantity,
  calculateRemainingParts,
} from "../utils/partsCalculationUtils";

export interface UseSelectedSuppliersReturn {
  selectedSuppliersInBucket: SelectedSupplier[];
  remainingPartsToFulfill: PartQuantity[];
  isOrderFullyFulfilled: boolean;
  orderFulfillmentPercentage: number;
  totalRemainingQuantity: number;
  addSupplierToSelectionBucket: (
    supplier: Omit<SelectedSupplier, "selectedAt">
  ) => void;
  removeSupplierFromSelectionBucket: (supplierId: string) => void;
  updateSupplierInSelectionBucket: (
    supplierId: string,
    updates: Partial<SelectedSupplier>
  ) => void;
  clearAllSuppliersFromSelectionBucket: () => void;
  getSupplierByIdFromSelectionBucket: (
    supplierId: string
  ) => SelectedSupplier | undefined;
}

export const useSelectedSuppliers = (
  originalParts: PartQuantity[]
): UseSelectedSuppliersReturn => {
  const [selectedSuppliersInBucket, setSelectedSuppliersInBucket] = useState<
    SelectedSupplier[]
  >([]);

  const remainingCalculation = useMemo(() => {
    return calculateRemainingParts(originalParts, selectedSuppliersInBucket);
  }, [originalParts, selectedSuppliersInBucket]);

  const addSupplierToSelectionBucket = useCallback(
    (supplier: Omit<SelectedSupplier, "selectedAt">) => {
      setSelectedSuppliersInBucket((prev) => {
        const existingIndex = prev.findIndex(
          (s) => s.supplierId === supplier.supplierId
        );

        if (existingIndex >= 0) {
          const updated = [...prev];
          updated[existingIndex] = {
            ...prev[existingIndex],
            ...supplier,
            selectedAt: prev[existingIndex].selectedAt,
          };
          return updated;
        } else {
          return [...prev, { ...supplier, selectedAt: new Date() }];
        }
      });
    },
    []
  );

  const removeSupplierFromSelectionBucket = useCallback(
    (supplierId: string) => {
      setSelectedSuppliersInBucket((prev) =>
        prev.filter((s) => s.supplierId !== supplierId)
      );
    },
    []
  );

  const updateSupplierInSelectionBucket = useCallback(
    (supplierId: string, updates: Partial<SelectedSupplier>) => {
      setSelectedSuppliersInBucket((prev) =>
        prev.map((supplier) =>
          supplier.supplierId === supplierId
            ? { ...supplier, ...updates }
            : supplier
        )
      );
    },
    []
  );

  const clearAllSuppliersFromSelectionBucket = useCallback(() => {
    setSelectedSuppliersInBucket([]);
  }, []);

  const getSupplierByIdFromSelectionBucket = useCallback(
    (supplierId: string) => {
      return selectedSuppliersInBucket.find((s) => s.supplierId === supplierId);
    },
    [selectedSuppliersInBucket]
  );

  return {
    selectedSuppliersInBucket,
    remainingPartsToFulfill: remainingCalculation.remainingParts,
    isOrderFullyFulfilled: remainingCalculation.isFullyFulfilled,
    orderFulfillmentPercentage: remainingCalculation.fulfillmentPercentage,
    totalRemainingQuantity: remainingCalculation.totalRemainingQuantity,
    addSupplierToSelectionBucket,
    removeSupplierFromSelectionBucket,
    updateSupplierInSelectionBucket,
    clearAllSuppliersFromSelectionBucket,
    getSupplierByIdFromSelectionBucket,
  };
};
